import { Box, Chip, IconButton, Typography } from "@mui/material";
import { PeopleAltOutlined } from "@mui/icons-material";
import { useState } from "react";
import useEvent from "react-use-event-hook";

import { Table } from "../../components/Table";
import {
  TableColumnInterface,
  TableSort,
} from "../../components/Table/TableTypes";

import { useRequestsContext } from "../../contexts/requests/RequestsContext";
import { useRequestsActions } from "../../contexts/requests/UseRequestsActions";

import Request, {
  RequestDataTableFields,
  requestDataOrderTransform,
} from "../../core/models/Request";
import {
  DEFAULT_PAGE_SIZE,
  modelDataOrderTransform,
} from "../../core/services/network/NetworkTypes";

import { useSearchHelper } from "../../hooks/useSearchHelper";

import { DateFormat, getFormattedDate } from "../../util/utils";

import { NewRequestModal } from "./newRequestModal.container";
import { GRAY_BLACK } from "../../themes/colors";
import { RoutePath } from "../../defines/route-path";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../contexts/authentication/AuthenticationContext";
import { Role } from "../../core/models/User";

const Requests = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuthContext();
  const isBusinessUser = userInfo?.permissionRole === Role.Business;

  const { requestsResponseData, requestLoading, setSelectedRequest } =
    useRequestsContext();
  const { dispatchGetRequests } = useRequestsActions();
  const [selectedRequestId, setSelectedRequestId] = useState<string>();
  const [openNewRequest, setOpenNewRequest] = useState<boolean>(false);

  //#region Load requests
  const loadRequests = (
    page: number,
    ordering?: TableSort<RequestDataTableFields>,
    search?: string
  ) => {
    dispatchGetRequests({
      filter: { search },
      ordering: modelDataOrderTransform(ordering, requestDataOrderTransform),
      page,
      size: DEFAULT_PAGE_SIZE,
    }).catch();
  };
  //#endregion

  //#region Table config
  const columns: Array<TableColumnInterface<RequestDataTableFields>> = [
    { id: "title", label: "request.title", sortable: true },
    { id: "id", label: "request.id", sortable: false },
    {
      id: "executed",
      label: "request.executed",
      sortable: true,
      handler: (row: Request) => {
        return row.dataSharingStats ? row.dataSharingStats.executed : "-";
      },
    },
    {
      id: "shared",
      label: "request.shared",
      sortable: true,
      handler: (row: Request) => (
        <Box className="flex items-center justify-center">
          <Typography variant="body2" fontWeight={500} color={GRAY_BLACK}>
            {row.dataSharingStats.shared}
          </Typography>
          <Chip
            color={"warning"}
            label={calculateSharingPercentage(row)}
            className="ml-4"
          />
        </Box>
      ),
    },
    {
      id: "created",
      label: "request.createdOn",
      sortable: true,
      handler: (row: Request) => {
        return row.created
          ? getFormattedDate(row.created, DateFormat.DayOfMonth)
          : "-";
      },
    },
  ];
  //#endregion

  //#region
  const handleOnClickRow = (row: Request) => {
    setSelectedRequest(row);
    let url = !isBusinessUser
      ? RoutePath.VIEW_REQUEST.replace(":id", row.id) + "steps"
      : RoutePath.INVITATIONS.replace(":id", row.id);
    navigate(url);
  };

  const handleOnClickAction = () => {
    setSelectedRequestId("");
    setSelectedRequest(undefined);
    setOpenNewRequest(true);
  };
  //#endregion

  //#region helper function
  function calculateSharingPercentage(row: Request) {
    const shared = row.dataSharingStats.shared;
    const executed = row.dataSharingStats.executed;
    if (executed + shared === 0) return "0%"; // Avoid division by zero
    return Math.round((shared / (executed + shared)) * 100) + "%";
  }
  //#endregion

  //#region Search config
  const onSearch = useEvent(({ page, ordering, search }) => {
    loadRequests(page, ordering, search);
  });

  const searchHelper = useSearchHelper<RequestDataTableFields>({
    onSearch,
  });
  //#endregion
  return (
    <>
      <Box
        id="pageWrapper"
        className="w-full flex flex-col flex-1 space-y-[1.875rem]"
      >
        <Box className="h-full">
          <Table
            {...searchHelper.tableProps}
            columns={columns}
            data={requestsResponseData?.data}
            count={requestsResponseData?.count}
            loading={requestLoading}
            actionTitle={!isBusinessUser ? "table.add" : undefined}
            onSearch={searchHelper.setSearch}
            onClickAction={handleOnClickAction}
            onClickRow={handleOnClickRow}
          />
        </Box>
      </Box>
      {openNewRequest && (
        <NewRequestModal
          open={openNewRequest}
          id={selectedRequestId}
          onClose={() => setOpenNewRequest(false)}
          data-testid={"new.request.modal"}
        />
      )}
    </>
  );
};

export { Requests };
